import { useCallback, useEffect, useMemo, useState } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { Repeat } from "lucide-react";
import { Field } from "../../../../../../../../../components/shared/Form";
import { Input } from "../../../../../../../../../components/ui/input";
import Button from "../../../../../../../../../components/shared/Button";
import Dialog from "../../../../../../../../../components/shared/Dialog";
import DateInput from "../../../../../../../../../components/shared/inputs/DateInput";
import Select from "../../../../../../../../../components/shared/inputs/Select";
import Weekly from "./Weekly";
import { useDateFilters } from "../../../../../../../../../components/shared/filters/DateFilters/context/useDateFilters";
import { useVisible } from "../../../../../../../../../utils/hooks/useVisible";
import { getOrderedWeekdays } from "../../../../../../helpers/getOrderedWeekdays";
import { firstToUpper } from "../../../../../../../../../utils/helpers/string";
import { cn } from "../../../../../../../../../lib/utils";
import type { Weekday } from "../../../../../../../../../components/shared/inputs/WeekdaySelect/types";

function DoesNotRepeat() {
    const { t } = useTranslation();
    const { control, setValue } = useFormContext();
    const { mode } = useDateFilters();
    const { open, visible, close } = useVisible();
    const [snapshot, setSnapshot] = useState<{ recurrence: any; days: Weekday[] } | null>(null);
    const endTypeField = useController({ control, name: "recurrence.endType" });

    const recurrence = useWatch({ control, name: "recurrence" })
    const date = useWatch({ control, name: "date" });
    const frequency = useWatch({ control, name: "recurrence.frequency" });
    const endType = useWatch({ control, name: "recurrence.endType" });
    const endDate = useWatch({ control, name: "recurrence.endDate" });
    const orderedWeekdays = getOrderedWeekdays();

    const defaultDaysValue = useMemo(() => {
        if (recurrence?.daysOfWeek?.length > 0) {
            return recurrence.daysOfWeek;
        } else {
            const dayIndex = mode === "month"
                ? moment(date).startOf("week").weekday()
                : moment(date).weekday();
            return [orderedWeekdays[dayIndex]];
        }
    }, [recurrence?.daysOfWeek, mode, orderedWeekdays, date]);

    const [days, setDays] = useState<Weekday[]>(defaultDaysValue);

    useEffect(() => {
        if (frequency === "weekly") {
            setValue("recurrence.daysOfWeek", days);
        }
    }, [days, frequency, setValue]);

    const title = useMemo(() => {
        if (!recurrence) return t("does-not-repeat");

        const { interval, occurrences, repeatOn } = recurrence;
        const formattedDate = moment(endDate).format("DD MMMM YYYY");
        const dayList = (days || []).map((day) => t(day)).join(", ");

        const isSingle = interval === 1;
        const isAfter = endType === "after";

        const getKey = (base: string) => {
            if (isAfter) return isSingle ? base : `every-amount-${base}`;
            return isSingle ? `${base}-until` : `every-amount-${base}-until`;
        };

        switch (frequency) {
            case "daily":
                return t(getKey("day-repeat"), {
                    amount: interval,
                    repeatAmount: occurrences,
                    repeatDate: formattedDate,
                });

            case "weekly":
                return t(getKey("week-repeat"), {
                    amount: interval,
                    days: dayList,
                    repeatDate: formattedDate,
                    repeatAmount: occurrences
                });

            case "monthly":
                return t(getKey("month-repeat"), {
                    amount: interval,
                    repeatOn: t(repeatOn),
                    repeatAmount: occurrences,
                    repeatDate: formattedDate,
                });

            default:
                return t("does-not-repeat");
        }
    }, [recurrence, days, endDate, endType, frequency, t]);

    const amountConfig = useMemo(() => {
        let arrayLength: number = 30;
        switch (frequency) {
            default:
            case "daily": {
                arrayLength = 30;
                break;
            }
            case "weekly": {
                arrayLength = 20;
                break;
            }
            case "monthly": {
                arrayLength = 12;
                break;
            }
        }
        return new Array(arrayLength).fill(null).map((_: null, i: number) => i + 1);
    }, [frequency]);

    const intervalLabel = useMemo(() => {
        switch (frequency) {
            default:
            case "daily": {
                return firstToUpper(t("days"));
            }
            case "weekly": {
                return t("weeks");
            }
            case "monthly": {
                return t("months");
            }
        }
    }, [frequency, t]);

    const onRepeatTypeChange = useCallback((value?: string) => {
        endTypeField.field.onChange(value);
        if (value === "on") {
            setValue("recurrence.endDate", moment(date).add(5, "days").toDate());
        } else {
            setValue("recurrence.occurrences", 5);
        }
    }, [setValue, date, endTypeField.field]);

    const openDialog = useCallback(() => {
        setSnapshot({ recurrence, days });
        if (!recurrence) {
            setValue("recurrence", {
                frequency: "weekly",
                interval: 1,
                endType: "after",
                occurrences: 5,
            }, { shouldDirty: true });
        }
        open();
    }, [open, recurrence, days, setValue]);

    const onCancel = useCallback(() => {
        if (snapshot) {
            setValue("recurrence", snapshot.recurrence, { shouldDirty: true });
            setDays(snapshot.days);
        }
        close();
    }, [setValue, close, snapshot]);

    const reset = useCallback(() => {
        setValue("recurrence", null, { shouldDirty: true });
        setDays(defaultDaysValue);
        setSnapshot({ recurrence: null, days: defaultDaysValue });
    }, [setValue, defaultDaysValue]);

    return (
        <>
            <Button
                variant="ghost"
                className="tw-mb-6 tw-min-w-0 tw-max-w-full tw-justify-start tw-text-primary"
                onClick={openDialog}
            >
                <Repeat className="tw-shrink-0" />
                <span className="tw-min-w-0 tw-truncate">{title}</span>
            </Button>

            <Dialog
                title={t("repeating-shift-settings")}
                open={visible}
                close={onCancel}
                size="2xl"
            >
                <div className="tw-flex tw-items-start tw-gap-3 tw-flex-wrap tw-mb-4 tw-w-full">
                    <Field name="recurrence.frequency" className="tw-mb-0" shouldUnregister={false}>
                        <Select>
                            <Select.Item value="daily">
                                {t("daily")}
                            </Select.Item>
                            <Select.Item value="weekly">
                                {t("weekly")}
                            </Select.Item>
                        </Select>
                    </Field>
                    <div className="tw-text-neutral-900 tw-text-sm tw-pt-2">{t("every")}</div>
                    <Field name="recurrence.interval" className="tw-mb-0" shouldUnregister={false}>
                        <Select>
                            {amountConfig.map((item: number) => (
                                <Select.Item key={item} value={String(item)}>
                                    {item}
                                </Select.Item>
                            ))}
                        </Select>
                    </Field>
                    <div className="tw-text-neutral-900 tw-text-sm tw-pt-2">{intervalLabel}</div>
                </div>

                {frequency === "weekly" && (
                    <Weekly days={days} setDays={setDays} />
                )}

                <div className="tw-flex tw-items-start tw-gap-2 tw-mb-4">
                    <div className="tw-text-neutral-900 tw-text-sm tw-pt-2">{t("end-repeat")}</div>
                    <Select
                        className="tw-w-25"
                        value={endTypeField.field.value}
                        onChange={onRepeatTypeChange}
                    >
                        <Select.Item value="after">{firstToUpper(t("after"))}</Select.Item>
                        <Select.Item value="on">{firstToUpper(t("on"))}</Select.Item>
                    </Select>

                    {endType === "after" ? (
                        <Field
                            className="tw-mb-0"
                            name="recurrence.occurrences"
                            deps={["recurrence.endType"]}
                            shouldUnregister={false}
                        >
                            <Input className="tw-w-17" type="number" min={0} max={amountConfig.length} />
                        </Field>
                    ) : (
                        <Field
                            name="recurrence.endDate"
                            className="tw-mb-0"
                            deps={["recurrence.endType"]}
                            shouldUnregister={false}
                        >
                            <DateInput
                                className={cn(
                                    endDate && moment(endDate).isSameOrBefore(moment(), "date") &&
                                    "tw-text-rose-500"
                                )}
                            />
                        </Field>
                    )}

                    {endType === "after" && (
                        <div className="tw-text-neutral-900 tw-text-sm tw-pt-2">{t("occurrences")}</div>
                    )}
                </div>

                <div className="tw-flex tw-justify-end tw-gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onCancel}
                    >
                        {t("cancel")}
                    </Button>
                    {recurrence && (
                        <Button variant="outline" onClick={reset}>
                            {t("reset")}
                        </Button>
                    )}
                    <Button onClick={close}>
                        {t("save")}
                    </Button>
                </div>
            </Dialog>
        </>
    );
}

export default DoesNotRepeat;
